import {
	createListenerMiddleware,
	createSlice,
	PayloadAction,
} from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorOptions,
	CreatorProduct,
	CreatorRendererOptions,
	CreatorSelectedAttributes,
	SelectedAttributeValue,
} from '@/types/creator';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { pick } from 'lodash';
import { Component } from '@/api/ComponentApi';
import { UUID } from '@/types/types';
import { ChatMessage } from '@/api/ChatApi';
import {
	getInitialSelectedComponents,
	getInitialSelectedAttributes,
} from '@/utils/utilities';
import ProductApi, { Product, ProductStatus } from '@/api/ProductApi';
import APP_CONFIG from '@/configs/appConfig';

export interface CreatorSliceState {
	product: CreatorProduct;
	options: CreatorOptions;
	renderer: CreatorRendererOptions;
	chatMessages: ChatMessage[];
}

const initialState: CreatorSliceState = {
	product: {
		id: crypto.randomUUID(),
		netPrice: 0,
		grossPrice: 0,
		productType: null,
		status: ProductStatus.NOT_ADDED,
		updatedAt: '',
		createdAt: '',
		productComponents: [],
		options: '',
	},
	options: {
		previewSrc: '',
		isInitialized: false,
		productTypeId: '65315629-8fbe-4549-a00c-dba312fd45b9',
		selectedComponents: {},
		selectedAttributes: {},
		selectedAttributesHistory: [],
		quantity: 1,
		translateMode: 'translate',
	},
	renderer: {
		zoomLevel: RENDERER_CONFIG.INITIAL_ZOOM,
		hasModelLoaded: false,
	},
	chatMessages: [
		{
			id: crypto.randomUUID(),
			role: 'assistant',
			content:
				'Cześć! Jestem Twoim asystentem AI. Wpisz lub powiedz czego szukasz.',
			createdAt: new Date().toISOString(),
		},
	],
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorSliceState>>
		) => {
			Object.assign(state, payload);
		},
		updateProduct: (
			state,
			{ payload }: PayloadAction<Partial<CreatorProduct>>
		) => {
			Object.assign(state.product, payload);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		setPreviewSrc: (state, { payload }: PayloadAction<string>) => {
			if (state.options.previewSrc)
				URL.revokeObjectURL(state.options.previewSrc);

			state.options.previewSrc = payload;
		},
		updateRenderer: (
			state,
			{ payload }: PayloadAction<Partial<CreatorRendererOptions>>
		) => {
			Object.assign(state.renderer, payload);
		},
		resetCreator: (state) => {
			Object.assign(state.product, {
				netPrice: 0,
				grossPrice: 0,
				productComponents: [],
			});

			const selectedComponents = getInitialSelectedComponents(
				state.product.productType!.componentCategories
			);
			const selectedAttributes = getInitialSelectedAttributes(
				state.product.productType!.componentCategories
			);

			Object.assign(state.options, {
				selectedComponents,
				selectedAttributes,
				quantity: 1,
			});
		},
		changeSelectedComponent: (
			state,
			{
				payload,
			}: PayloadAction<{
				componentCategoryId: UUID;
				component: Component | null;
			}>
		) => {
			state.options.selectedComponents[payload.componentCategoryId] =
				payload.component;
		},
		changeSelectedAttributes: (
			state,
			{
				payload,
			}: PayloadAction<{
				componentCategoryId: UUID;
				attributeId: UUID;
				attributeValueId: SelectedAttributeValue;
			}>
		) => {
			state.options.selectedAttributes[payload.componentCategoryId][
				payload.attributeId
			] = payload.attributeValueId;
		},
		addToHistory: (
			{ options },
			{ payload }: PayloadAction<CreatorSelectedAttributes>
		) => {
			if (
				options.selectedAttributesHistory.length ===
				APP_CONFIG.HISTORY_MAX_LENGTH
			)
				options.selectedAttributesHistory.shift();

			options.selectedAttributesHistory.push(payload);
		},
		undoAttributesSelection: ({ options }) => {
			const prev = options.selectedAttributesHistory.pop();
			if (!prev) return;

			options.selectedAttributes = prev;
		},
		insertMessage: (state, { payload }: PayloadAction<ChatMessage>) => {
			state.chatMessages.push(payload);
		},
	},
});

export const {
	updateCreator,
	updateProduct,
	updateOptions,
	setPreviewSrc,
	resetCreator,
	changeSelectedComponent,
	changeSelectedAttributes,
	addToHistory,
	undoAttributesSelection,
	updateRenderer,
	insertMessage,
} = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
